import { getFundAllocationsService } from './allocation-service';
import { getRDFRecommendation } from './rdf-service';
import {
  withDefault,
  APP_VERSION,
  isEmpty,
  isEmptyObject
} from '../../../core/util';
import {
  parsePhone,
  applicationStatusTypes,
  accountTypes,
  formatAccountNumber,
  setTaxIDType,
  consumerTypes,
  investmentMethods,
  setRJCustomerPhones,
  getMailingAddressDifferentFlag
} from '../utils';
import {
  PartialStateStore,
  StateStore
} from '../../../core/types/application-state';
import {
  convertAppDataToProfileDataTransfer,
  convertProfileDataToAppDataTransfer
} from '../../transfer-flows/util';
import {
  convertAppDataToProfileDataRollover,
  convertProfileDataToAppDataRollover
} from '../../rollover/util';
import {
  convertAppDataToProfileDataContributory,
  convertProfileDataToAppDataContributory,
  getIRAAccountType
} from '../../contributory/util';
import {
  convertProfileDataToTrustAppData,
  convertTrustAppDataToProfileData
} from '../../taxable-flows/trust/utils';
import {
  convertTaxableAppDataToProfileData,
  convertTaxableProfileDataToAppData
} from '../../taxable-flows/shared/util';
import { parseInvestmentsForRemittanceForm } from './check-remittance.service';
import { ServiceError } from '.';
import { DeepPartialProfileArgs } from '../../../core/types/return-journey-profile';
import { poBoxLine1Validation, poBoxLine2Validation } from '../validations';

export const DEFAULT_SERVICE_STRING = 'UNKNOWN';

// application submission time out error code
export const submissionTimeOutErrorCode = [504];

export const handleBankRoutingNumberError = (error: any, payload: any): any => {
  if (error?.status === 500) return { error };

  throw new ServiceError(
    `A service call has failed with status: ${
      error.serviceErrorCode
    } (payload: ${JSON.stringify(payload)})`
  );
};

export const isNonArrayObject = (x: any): boolean =>
  Boolean(x && typeof x === 'object' && !Array.isArray(x));

export const isArrayOrObject = (x: any): boolean =>
  Array.isArray(x) || Boolean(x && typeof x === 'object');

export const purgeEmptyFields = (obj: any, isOCRPDF = false): any => {
  if (Array.isArray(obj)) {
    const filtered = obj
      .map(val =>
        isArrayOrObject(val) ? purgeEmptyFields(val, isOCRPDF) : val
      )
      .filter(val => !isEmptyObject(val) && !isEmpty(val));
    // If the root array is empty after filtering, return undefined
    return filtered.length > 0 ? filtered : undefined;
  }

  if (!isNonArrayObject(obj)) {
    // For non-object, non-array values, return as is
    return obj;
  }

  /** purge empty data points of payloads
   * EXCEPT in the case of OCR PDF generation for beneficiary objects,
   * as the empty data arrays are necessary for the BE to consume
   */
  const ocrPDFBeneficiaryKeys = [
    'primarybeneficiaries',
    'secondarybeneficiaries'
  ];
  const result = Object.entries(obj)
    .map(([key, value]) => {
      if (isOCRPDF && ocrPDFBeneficiaryKeys.includes(key.toLowerCase())) {
        return [key, value];
      }
      return [
        key,
        isArrayOrObject(value) ? purgeEmptyFields(value, isOCRPDF) : value
      ];
    })
    .reduce((accum, [key, value]) => {
      if (
        ocrPDFBeneficiaryKeys.includes(key.toLowerCase()) ||
        (!isEmpty(value) && !isEmptyObject(value))
      ) {
        return {
          ...accum,
          [key]: value
        };
      }
      return accum;
    }, {});

  // If the root object is empty after purging, return undefined
  return Object.keys(result).length > 0 ? result : undefined;
};

export const removePropertiesFromObject = (
  obj: any,
  propertiesToRemove: Array<string>
): any => {
  return Object.entries(obj)
    .map(([key, value]) => [
      key,
      isNonArrayObject(value)
        ? removePropertiesFromObject(value, propertiesToRemove)
        : value
    ])
    .reduce((accum, [key, value]) => {
      if (!propertiesToRemove.includes(key)) {
        return {
          ...accum,
          [key]: value
        };
      }

      return accum;
    }, {});
};

export const removeExtraAddressFields = (obj: any): any => {
  const addressFieldsToRemove = [
    'addressType',
    'country',
    'foreignAddress',
    'militaryAddress',
    'usaddress'
  ];
  return removePropertiesFromObject(obj, addressFieldsToRemove);
};

export const appInvestmentsToProfileInvestments = (
  appInvestments: any[] | undefined
): any =>
  withDefault(appInvestments, []).map(
    ({ ticker, trpCode, fundingDetails, fundName }) => ({
      fundName,
      ticker,
      trpCode,
      fundingAmount: fundingDetails?.fundingAmount,
      fundingAmountPercentage: fundingDetails?.fundingAmountPercentage
    })
  );

export const appTaxableInvestmentsToProfileInvestments = (
  investments: any[] | undefined
): any =>
  withDefault(investments, []).map(
    ({
      name,
      ticker,
      trpCode,
      amount,
      fundingAmount,
      percent,
      fundingAmountPercentage
    }) => ({
      fundName: name,
      ticker,
      trpCode,
      fundingAmount: withDefault(amount, fundingAmount),
      fundingAmountPercentage: withDefault(percent, fundingAmountPercentage)
    })
  );

export const appAccountsToProfileAccounts = (
  appAccounts: any[] | undefined
): any =>
  withDefault(appAccounts, []).map(
    ({ investmentVehicle, fundingDetails, agreementID }) => ({
      ticker: investmentVehicle?.ticker,
      trpCode: investmentVehicle?.trpCode,
      fundingAmount: fundingDetails?.fundingAmount,
      fundingAmountPercentage: fundingDetails?.fundingAmountPercentage,
      agreementID
    })
  );

export const appTaxableAccountsToProfileAccounts = (
  appAccounts: any[] | undefined
): any =>
  withDefault(appAccounts, []).map(({ investmentVehicle, agreementID }) => ({
    ticker: investmentVehicle?.ticker,
    trpCode: investmentVehicle?.trpCode,
    fundingAmount: investmentVehicle?.amount,
    fundingAmountPercentage: investmentVehicle?.percent,
    agreementID
  }));

export const custodianHoldings = (appHoldings: any[] | undefined): any =>
  withDefault(appHoldings, []).map(
    ({
      accountName,
      accountNumber,
      estimatedTransferAmount,
      liquidationOption,
      cusip
    }) => ({
      fundName: accountName,
      accountNumber: formatAccountNumber(accountNumber),
      amount: estimatedTransferAmount,
      fundingPortion: liquidationOption,
      cusip
    })
  );

// TODO: remove populateCustodianInfo once all MF flows are built
// return journey mappers should not use catch-all functions
// when the info inside differs by account type
export const populateCustodianInfo = (store: PartialStateStore): any[] => {
  const { app } = store;
  const partialCustodianInfo = {
    custodianID: app?.custodian?.custodianID,
    custodianName: app?.custodian?.custodianName,
    address: {
      street1: withDefault(app?.custodian?.custodianAddress?.street1, ''),
      street2: withDefault(app?.custodian?.custodianAddress?.street2, ''),
      street3: '',
      state: withDefault(app?.custodian?.custodianAddress?.state, ''),
      city: withDefault(app?.custodian?.custodianAddress?.city, ''),
      country: '',
      zip: withDefault(app?.custodian?.custodianAddress?.zip, ''),
      usaddress: true,
      foreignAddress: false,
      militaryAddress: false
    },
    phone: store?.custodianCustomerServicePhone,
    holdings: withDefault(custodianHoldings(app?.externalAccounts), []),
    toraParticipant: store?.toraParticipant,
    // TODO [2021-01-22]: there needs to be a story to base
    // useAccountNumberForAllHoldings
    // on the actual checkbox value in the UI
    // rather than hardcoding it to false every time
    useAccountNumberForAllHoldings: false,
    brokerageAccount: store?.fundsInBrokerage,
    liquidAssets: store?.preliquidated
  };
  const custodianInfo = purgeEmptyFields(partialCustodianInfo);
  return [custodianInfo];
};

export const populateProfileAdditionalCustomers = (
  additionalCustomers: any[] | undefined
): any =>
  withDefault(additionalCustomers, []).map(person => ({
    customerType: person.customerType,
    firstName: person.name.firstName,
    middleName: withDefault(person.name.middleName, ''),
    lastName: person.name.lastName,
    dateOfBirth: person.dob,
    taxID: person.taxID,
    citizenship: person.citizenship,
    residentialAddress: person.residentialAddress
  }));

export const populateAppAdditionalCustomers = (
  additionalCustomers: any[] | undefined
): any =>
  withDefault(additionalCustomers, []).map(person => ({
    customerType: person.customerType,
    name: {
      firstName: person.firstName,
      middleName: withDefault(person.middleName, ''),
      lastName: person.lastName
    },
    dob: person.dateOfBirth,
    taxID: person.taxID,
    citizenship: person.citizenship,
    residentialAddress: person.residentialAddress
  }));

export const setInvestmentFundName = (
  chosenInvestments: any,
  investments: any
): any =>
  investments?.map(inv => {
    const matchedFund = chosenInvestments?.find(ci => ci.ticker === inv.ticker);
    return matchedFund
      ? {
          ...inv,
          // account for profile service setting namme to fundName
          fundName: matchedFund.name || matchedFund.fundName
        }
      : {
          ...inv,
          fundName: ''
        };
  });

export const getResidentialAddressForEnabled = (
  customerAddress,
  profileAddress
): any => {
  if (isEmptyObject(customerAddress)) {
    return profileAddress;
  }
  const { street1, street2 } = customerAddress;
  if (poBoxLine1Validation(street1) || poBoxLine2Validation(street2))
    return profileAddress;
  return customerAddress;
};

export const filterMailingAddressForEnabled = (addresses): any => {
  // parse single mailing address out of array of addresses given through customer endpoint
  if (Array.isArray(addresses)) {
    const secondaryAddress = addresses?.find(
      addy => addy?.addressType === 'secondary'
    );
    return {
      street1: secondaryAddress?.street1,
      street2: secondaryAddress?.street2,
      state: secondaryAddress?.state,
      zip: secondaryAddress?.zip,
      city: secondaryAddress?.city
    };
  }

  return addresses;
};

export const convertToChosenInvestments = async (
  investments,
  investmentMethod,
  dob = '',
  enabled = false
): Promise<any> => {
  let chosenInvestments = [];

  if (investments.length > 0) {
    const tickers = investments.map(f => f.ticker);
    const fundDetails = await getFundAllocationsService({ tickers });

    const rdfIClassFund =
      investmentMethod === investmentMethods.RDF && enabled
        ? await getRDFRecommendation({
            birthDate: dob,
            amount: 0,
            isSummitMember: true
          })
        : {};

    chosenInvestments = fundDetails.map(inv => {
      const matchingFund = investments.find(f => f.ticker === inv.ticker);

      const correspondingFund =
        investmentMethod === investmentMethods.RDF
          ? rdfIClassFund
          : inv.correspondingFund;

      // taxable specific account will not have a fundingDetails object within newInvestments
      // all other account types will have fundingAmount and fundingAmountPercentage withing fundingDetails object
      return {
        ...inv,
        ...(!inv.iClass && { correspondingFund }),
        amount:
          matchingFund?.fundingAmount ||
          matchingFund?.fundingDetails?.fundingAmount,
        percent:
          matchingFund?.fundingAmountPercentage ||
          matchingFund?.fundingDetails?.fundingAmountPercentage
      };
    });
  }

  return chosenInvestments;
};

const parseCustodianAddressForForm = (
  custodian,
  branchAddressRequired
): any => {
  let address = withDefault(custodian?.custodianAddress, {});
  // if custodian needs special address then utilize the stored address for custodian
  // this data is stored with `commitCustodianBranchAddress` on continuation of Custodian Address Details Screen
  if (branchAddressRequired) {
    address = {
      street1: withDefault(address?.street1?.trim(), ''),
      state: withDefault(address?.state?.trim(), ''),
      zip: withDefault(address?.zip?.trim(), ''),
      city: withDefault(address?.city?.trim(), '')
    };
  }
  // otherwise send blank address so service can build address for form
  // fix implemented for DE44252
  else {
    address = { street1: '', city: '', state: '', zip: '' };
  }

  return address;
};

const parseCustodianHoldingsForForm = (holdings): any =>
  withDefault(holdings, []).map(
    ({
      accountName,
      accountNumber,
      estimatedTransferAmount,
      liquidationOption
    }) => ({
      fundName: accountName,
      accountNumber: formatAccountNumber(accountNumber),
      amount: estimatedTransferAmount,
      fundingPortion: liquidationOption
    })
  );

export const parseInvestmentsForForm = (
  chosenInvestments: any,
  investments: any
): any =>
  investments?.map(inv => {
    const matchedFund = chosenInvestments?.find(ci => ci.ticker === inv.ticker);
    return {
      fundName: withDefault(matchedFund.name, matchedFund.fundName),
      fundingAmountPercentage: matchedFund.percent
    };
  });

export const convertAppDataToMutualFundFormData = (
  store: PartialStateStore
): any => {
  const { app, chosenInvestments, loadedData } = store;
  const newInvestments = appInvestmentsToProfileInvestments(
    app?.newInvestments
  );
  const existingAccounts = appAccountsToProfileAccounts(app?.existingAccounts);
  // add fund name to investment allocations; needed for form request
  const investmentAllocations = parseInvestmentsForForm(chosenInvestments, [
    ...existingAccounts,
    ...newInvestments
  ]);

  return {
    custodian: {
      custodianID: app?.custodian?.custodianID,
      custodianName: app?.custodian?.custodianName,
      address: parseCustodianAddressForForm(
        app?.custodian,
        loadedData?.branchAddressRequired
      ),
      phone: '',
      preliquidatedAssets: store?.preliquidated,
      holdings: parseCustodianHoldingsForForm(app?.externalAccounts)
    },
    customer: {
      firstName: app?.customer?.name?.firstName,
      lastName: app?.customer?.name?.lastName,
      middleName: withDefault(app?.customer?.name?.middleName, ''),
      taxID: withDefault(app?.customer?.taxID, ''),
      jointOwnerFirstName: '',
      jointOwnerLastName: '',
      phoneNumber: ''
    },
    investmentAllocations,
    sourceAccountType: `${app?.fromIRAAccountType}_IRA`,
    destinationAccountType: `${app?.toIRAAccountType}_IRA`,
    transferTrackingID: ''
  };
};

const getAccountTypeForCheckForm = ({ accountType, app }): any =>
  accountType === accountTypes.CONTRIBUTORY
    ? getIRAAccountType(app.iraAccountType)
    : accountType;

export const convertAppDataToCheckRemittanceFormData = (
  store,
  fundDetails = [],
  isFundLaterFlow = false
): any => {
  const {
    app: {
      customer: {
        name: { firstName, middleName = '', lastName },
        mailingAddress
      }
    },
    chosenInvestments,
    loadedData: { accounts = [] }
  } = store;

  const accountType = getAccountTypeForCheckForm(store);

  return {
    accountType,
    customer: {
      firstName,
      middleName,
      lastName
    },
    address: {
      street1: mailingAddress?.street1,
      city: mailingAddress?.city,
      state: mailingAddress?.state,
      zip: mailingAddress?.zip
    },
    fundSelections: parseInvestmentsForRemittanceForm(
      chosenInvestments,
      fundDetails,
      accounts,
      isFundLaterFlow
    )
  };
};

// a catch-all to use when account type is not specified
export const convertAppDataToProfileDataFONASU = (
  store: PartialStateStore
): DeepPartialProfileArgs => {
  const { app, progressStepText, additionalCustomers } = store;

  const newInvestments = appInvestmentsToProfileInvestments(
    app?.newInvestments
  );

  const existingAccounts = appAccountsToProfileAccounts(app?.existingAccounts);

  // TODO [2020-05-25]: Let's make this a util to combine new + existing investments for profile service
  const investmentAllocations = [...existingAccounts, ...newInvestments];

  return {
    applicationID: withDefault(app?.profileApplicationID, ''),
    emailAddress: app?.customer?.primaryEmailAddress,
    applicationStatus: withDefault(
      app?.applicationStatus,
      applicationStatusTypes.IN_PROGRESS
    ),
    investmentAllocations,
    custodianInformation: app?.custodian ? populateCustodianInfo(store) : [],
    // ToDo: Below types need to be added and pulled in from store w.r.t current flow
    accountType: store?.accountType,
    fundingType: app?.fundingType,
    chargeType: app?.chargeType,
    toIRAAccountType: withDefault(app?.toIRAAccountType, app?.iraAccountType),
    fromIRAAccountType: app?.fromIRAAccountType,
    investAmount: withDefault(store?.amount, 0),
    investAmountPreviousYear: withDefault(
      store?.loadedData?.priorYearAmount,
      store?.priorYearAmount
    ),
    investAmountCurrentYear: withDefault(
      store?.loadedData?.currentYearAmount,
      store?.currentYearAmount
    ),
    firstName: app?.customer?.name?.firstName,
    lastName: app?.customer?.name?.lastName,
    middleName: withDefault(app?.customer?.name?.middleName, ''),
    residentialAddress: {
      street1: app?.customer?.residentialAddress?.street1,
      street2: app?.customer?.residentialAddress?.street2,
      state: app?.customer?.residentialAddress?.state,
      zip: app?.customer?.residentialAddress?.zip,
      city: app?.customer?.residentialAddress?.city
    },
    mailingAddress: {
      street1: app?.customer?.mailingAddress?.street1,
      street2: app?.customer?.mailingAddress?.street2,
      state: app?.customer?.mailingAddress?.state,
      zip: app?.customer?.mailingAddress?.zip,
      city: app?.customer?.mailingAddress?.city
    },
    citizenship: withDefault(app?.customer?.citizenship, 'UNKNOWN'),
    dateOfBirth: app?.customer?.dob,
    formerEmployer: withDefault(store?.loadedData?.formerEmployer, ''),
    taxID: withDefault(app?.customer?.taxID, ''),
    governingState: withDefault(app?.governingState, ''),
    phoneNumbers:
      app?.customer?.phones?.map(({ areaCode, exchange, lastFour, type }) => ({
        phoneNumber: `(${areaCode}) ${exchange}-${lastFour}`,
        phoneNumberType: type
      })) || [],
    additionalCustomers: populateProfileAdditionalCustomers(
      additionalCustomers
    ),
    investmentSelectionPath: app?.investmentMethod,
    rolloverType: withDefault(store?.loadedData?.rolloverType, ''),
    residentialAddressDifferentFromMailing: withDefault(
      store?.mailingAddressDifferent,
      false
    ),
    termsIDs: withDefault(
      app?.termsIDs?.map(id => id?.toString()),
      []
    ),
    consumerVersion: APP_VERSION,
    consumerName: store?.consumerName,
    loginID: withDefault(store?.loadedData?.loginID, ''),
    progressStepText,
    progressStep: withDefault(store?.progressStep, 0),
    associateLoginID: withDefault(store?.loadedData?.associateLoginID, '')
  };
};

// below function to convert data back to store for RJ
export const convertProfileDataToAppDataFONASU = (
  profile: DeepPartialProfileArgs,
  store: PartialStateStore
): PartialStateStore => {
  // build customer data for enabled users off customer endpoint data received (helps with prefilling)
  // exception is taxID/citiizenship data, which must be pulled from journey profile data
  // authenticated and new user will build customer data from journey profile
  const { app, enabled } = store;
  const phones = setRJCustomerPhones(
    profile?.phoneNumbers,
    app?.customer?.phones
  );

  const customerData = enabled
    ? {
        ...app?.customer,
        citizenship: profile?.citizenship,
        taxIDType: setTaxIDType(profile?.citizenship),
        residentialAddress: getResidentialAddressForEnabled(
          app?.customer?.residentialAddress,
          profile?.residentialAddress
        ),
        // pull email, phone and mailing address from profile service to
        // account for RPS only users submitting via FONASU RJ
        // since the customer call while doing RJ is not pulling them currently
        mailingAddress: withDefault(
          profile?.mailingAddress,
          filterMailingAddressForEnabled(app?.customer?.mailingAddress)
        ),
        primaryEmailAddress: withDefault(
          profile?.emailAddress,
          app?.customer?.primaryEmailAddress
        ),
        phones
      }
    : {
        name: {
          firstName: profile?.firstName,
          middleName: withDefault(profile?.middleName, ''),
          lastName: profile?.lastName
        },
        citizenship: profile?.citizenship,
        dob: profile?.dateOfBirth,
        taxID: profile?.taxID,
        taxIDType: setTaxIDType(profile?.citizenship),
        residentialAddress: {
          street1: profile?.residentialAddress?.street1,
          street2: profile?.residentialAddress?.street2,
          state: profile?.residentialAddress?.state,
          zip: profile?.residentialAddress?.zip,
          city: profile?.residentialAddress?.city
        },
        mailingAddress: {
          street1: profile?.mailingAddress?.street1,
          street2: profile?.mailingAddress?.street2,
          state: profile?.mailingAddress?.state,
          zip: profile?.mailingAddress?.zip,
          city: profile?.mailingAddress?.city
        },
        primaryEmailAddress: profile?.emailAddress,
        // [{phoneNumberType: string, phoneNumber: string}]
        // needs to be converted to ->
        // [{ areaCode: string, exchange: string, lastFour: string, type: string }]
        //
        // TODO [2020-06-20]: This is a big pain why can't backend use same format
        phones: profile?.phoneNumbers?.map(
          ({ phoneNumberType: type = '', phoneNumber = '' }) => ({
            ...parsePhone(phoneNumber),
            type
          })
        )
      };
  return {
    accountType: profile?.accountType,
    amount: profile?.investAmount,
    app: {
      customer: customerData,
      existingAccounts: [],
      // TODO [2020-06-20] If NASU only allows one custodian per application
      // why does the RJ profile service accept an array of custodians?
      // this should be consistent.
      // @note will Plaid allow us to support funding from multiple
      // custodians?
      externalAccounts: profile?.custodianInformation?.[0]?.holdings?.map(
        ({ fundName, accountNumber, amount, fundingPortion, cusip }) => ({
          accountName: fundName,
          accountType: 'MUTUAL_FUND',
          accountNumber,
          estimatedTransferAmount: amount,
          liquidationOption: fundingPortion,
          cusip
        })
      ),
      newInvestments: profile?.investmentAllocations?.map(
        ({ ticker, trpCode, fundingAmount, fundingAmountPercentage }) => ({
          ticker,
          trpCode,
          fundingDetails: {
            fundingAmount,
            fundingAmountPercentage
          }
        })
      ),
      investmentMethod: profile?.investmentSelectionPath,
      profileApplicationID: profile?.applicationID,
      toIRAAccountType: profile?.toIRAAccountType,
      fromIRAAccountType: profile?.fromIRAAccountType,
      termsIDs: withDefault(
        profile?.termsIDs?.map(id => id?.toString()),
        []
      ),
      custodian: {
        ...(!isEmpty(profile?.custodianInformation?.[0]?.custodianID) && {
          custodianID: profile?.custodianInformation?.[0]?.custodianID
        }),
        custodianName: profile?.custodianInformation?.[0]?.custodianName
      }
    },
    mailingAddressDifferent: getMailingAddressDifferentFlag(
      enabled,
      store,
      profile
    ),
    chosenInvestments: profile?.investmentAllocations,
    progressStepText: profile?.progressStepText,
    consumerVersion: profile?.consumerVersion,
    consumerName: withDefault(profile?.consumerName, consumerTypes.FONASU),
    preliquidated: profile?.custodianInformation?.[0]?.liquidAssets,
    fundsInBrokerage: profile?.custodianInformation?.[0]?.brokerageAccount,
    toraParticipant: profile?.custodianInformation?.[0]?.toraParticipant,
    custodianCustomerServicePhone: profile?.custodianInformation?.[0]?.phone
  };
};

/* eslint-disable */
// fix complexity
export const convertAppDataToProfileData = (
  store: StateStore
): DeepPartialProfileArgs => {
  const accountType: string = store?.accountType || 'UNKNOWN';
  // FONASU RJ Mapper
  if (store?.consumerName === consumerTypes.FONASU) {
    return convertAppDataToProfileDataFONASU(store);
  }
  // WebNASU RJ Mappers
  if (accountType === accountTypes.TRANSFER) {
    return convertAppDataToProfileDataTransfer(store);
  }
  if (accountType === accountTypes.ROLLOVER) {
    return convertAppDataToProfileDataRollover(store);
  }
  if (accountType === accountTypes.CONTRIBUTORY) {
    return convertAppDataToProfileDataContributory(store);
  }
  if (['INDIVIDUAL', 'JOINT_TENANT', 'UGMA_UTMA'].includes(accountType)) {
    return convertTaxableAppDataToProfileData(store);
  }
  if (accountType === accountTypes.TRUST) {
    return convertTrustAppDataToProfileData(store);
  }
  // Default Mapper
  return convertAppDataToProfileDataFONASU(store);
};

export const convertProfileDataToAppData = async (
  profile: DeepPartialProfileArgs,
  store: PartialStateStore
): Promise<PartialStateStore> => {
  const accountType: string = profile?.accountType || 'UNKNOWN';
  // FONASU/WebNASU RJ Mappers
  if (accountType === accountTypes.TRANSFER) {
    return await convertProfileDataToAppDataTransfer(profile, store);
  }
  if (accountType === accountTypes.ROLLOVER) {
    return await convertProfileDataToAppDataRollover(profile, store);
  }
  if (
    [
      'INDIVIDUAL',
      'JOINT_TENANT',
      'UGMA_UTMA',
      'TRANSFER_INDIVIDUAL',
      'TRANSFER_JOINT_TENANT',
      'TRANSFER_UGMA_UTMA'
    ].includes(accountType)
  ) {
    return await convertTaxableProfileDataToAppData(profile, store);
  }
  if (accountType === accountTypes.TRUST) {
    return await convertProfileDataToTrustAppData(profile, store);
  }
  if (accountType === accountTypes.CONTRIBUTORY) {
    return convertProfileDataToAppDataContributory(profile, store);
  }
  // Default Mapper
  return convertProfileDataToAppDataFONASU(profile, store);
};
/* eslint-enable */

export const handleSubmissionError = (error: any, payload: any): any => {
  const is504Timout: boolean = error?.status === 504;

  if (is504Timout) return { serviceErrorCode: 504 };

  throw new ServiceError(
    `A service call has failed with status: ${error} (payload: ${JSON.stringify(
      payload
    )})`
  );
};

const locateAemContentByKey = (data: any, searchKey: string): any => {
  let aemContent: any = null;

  if (!isNonArrayObject(data)) {
    return aemContent;
  }

  const entries = Object.entries(data);
  entries.forEach(entry => {
    const [key, value] = entry;
    if (key === searchKey) {
      aemContent = value;
    } else if (isNonArrayObject(value)) {
      // find nested
      const located = locateAemContentByKey(value, searchKey);
      if (located !== null) {
        aemContent = located;
      }
    }
  });
  return aemContent;
};

export const parsedPaperlessContent = (
  paperlessAemContent: any,
  contentKey = 'main'
): any => {
  if (isEmpty(paperlessAemContent)) return null;

  return locateAemContentByKey(paperlessAemContent, contentKey);
};
